/**
 * Block 12 PR-B Phase B3 Part C Step 19 — SectionFilling (Г).
 *
 * Только для Cabinet (Шкаф/Гардероб) и Balcony (Балкон) — methodology
 * `showFilling=true`. 7 fixed toggleable rows per методика §2.2 Г + §2.7 Г.
 *
 * Source: docs/prototypes/screen-2.12.3-cabinet-calc.html lines 440-446
 *         + screen-2.12.8-balcony-calc.html lines 420-426 (идентичный список).
 *
 * **Hotfix-2C Phase C3 Step 6 (2026-05-17) — Cabinet revert:** facade-radius
 * вернулся из Section Г обратно в Section В («Особые конструкции») per Schemas
 * v2 line 97-99. Отменяет hotfix-2B Phase B5 Step 12 (Bug 14 structure) — Cabinet
 * SectionFilling теперь рендерит ТОЛЬКО 7 filling rows (как Balcony), без
 * leading FacadeRadiusBlock.
 *
 * Reference: ADR-0020 §Q1, методика §2.2 Г + §2.7 Г, Schemas v2 «Шкаф» line 97-99.
 * Hotfix-1: Bug #2 + Bug #11 (Block 12 audit 2026-05-15) — 4 из 7 позиций были
 * галлюцинированы (Полка/Зеркало/Сейф/Ящик выдвижной); заменены на canonical
 * prototype items (Пантограф/Гладильная доска/Обувница/Держатель для шланга
 * пылесоса).
 */

import { SectionCard } from '../components/SectionCard';
import { SectionHeader } from '../components/SectionHeader';
import { ToggleableRow } from './ToggleableRow';

/** 7 fixed filling rows для Cabinet/Balcony per прототипы 2.12.3 + 2.12.8. */
const FILLING_ROWS = [
  { id: 'rod', label: 'Штанга', unit: 'шт' },
  { id: 'pantograph', label: 'Пантограф', unit: 'шт' },
  { id: 'basket-pullout', label: 'Корзина выдвижная', unit: 'шт' },
  { id: 'trousers', label: 'Брючница', unit: 'шт' },
  { id: 'ironing-board', label: 'Гладильная доска', unit: 'шт' },
  { id: 'shoe-rack', label: 'Обувница', unit: 'шт' },
  { id: 'vacuum-holder', label: 'Держатель для шланга пылесоса', unit: 'шт' },
] as const;

export interface SectionFillingProps {
  toggles: Record<string, boolean>;
  quantities: Record<string, number>;
  prices: Record<string, number>;
  onToggle: (id: string, on: boolean) => void;
  onQty: (id: string, qty: number) => void;
  onPrice: (id: string, price: number) => void;
  /** Optional Block 5 «Умный фурнитурщик» deeplink (Phase B4 wires Container). */
  onDeeplink?: (rowId: string) => void | Promise<void>;
  /**
   * PR-H1 Round 4 — per-row brand input для Section Г Filling (Cabinet/Balcony).
   * Symmetry с SectionAdditional Round 3 — brand value + onBrandChange прокидываются
   * в ToggleableRow. Render gated на `onBrand` — без него brand input скрыт.
   */
  brandSelections?: Record<string, string>;
  onBrand?: (id: string, brand: string) => void;
}

export function SectionFilling({
  toggles,
  quantities,
  prices,
  onToggle,
  onQty,
  onPrice,
  onDeeplink,
  brandSelections,
  onBrand,
}: SectionFillingProps) {
  const brandFor = (id: string): string | undefined =>
    typeof brandSelections?.[id] === 'string' ? brandSelections[id] : undefined;
  const onBrandFor = (id: string): ((brand: string) => void) | undefined =>
    onBrand ? (b) => onBrand(id, b) : undefined;

  return (
    <SectionCard testId="section-filling">
      <SectionHeader>Наполнение</SectionHeader>
      <div className="flex flex-col gap-3">
        {FILLING_ROWS.map((spec) => {
          const enabled = Boolean(toggles[spec.id]);
          return (
            <ToggleableRow
              key={spec.id}
              id={spec.id}
              label={spec.label}
              unit={spec.unit}
              enabled={enabled}
              qty={quantities[spec.id] ?? 0}
              price={prices[spec.id] ?? 0}
              onToggle={(on) => onToggle(spec.id, on)}
              onQty={(q) => onQty(spec.id, q)}
              onPrice={(p) => onPrice(spec.id, p)}
              deeplink="furniture"
              onDeeplink={onDeeplink ? () => onDeeplink(spec.id) : undefined}
              brand={brandFor(spec.id)}
              onBrandChange={onBrandFor(spec.id)}
            />
          );
        })}
      </div>
    </SectionCard>
  );
}

export { FILLING_ROWS };
